import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Divider, message, Modal, Popover, Row, Space, Tag, Typography } from 'antd'
import { CheckCircleOutlined, DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons'
import axios from 'axios'
import ExportCn from '../cn-softaccess/components/ExportCn'


interface CnAccessRow {
  softaccess_id: string
  file_label: string
  mspro: boolean
}

interface ConquestAccessRow {
  softaccess_id: string
  name: string
  iris_label: string
  pdv_label: string
  link: string
}

interface SoftwaresAccessesResponse {
  orga_id: string
  multiscan_soft_id: string
  cn: CnAccessRow[]
  conquest: ConquestAccessRow[]
}

interface OpenSoftwaresProps {
  orgaId: string
}

const { Title, Text } = Typography

export function OpenSoftwares({ orgaId }: OpenSoftwaresProps) {
  const [data, setData] = useState<SoftwaresAccessesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const loadAccesses = useCallback(async () => {
    setLoading(true)
    try {
      const { data: response } = await axios.get<SoftwaresAccessesResponse>(
        `/organisations/${orgaId}/softwares/list`,
      )
      setData(response)
    } catch (error) {
      console.error(error)
      message.error("Impossible de charger les accès logiciels")
    } finally {
      setLoading(false)
    }
  }, [orgaId])

  useEffect(() => {
    loadAccesses()
  }, [loadAccesses])

  const confirmDelete = useCallback(
    (softaccessId: string, deleteUrl: string) => {
      Modal.confirm({
        title: 'Supprimer l\'accès logiciel',
        content: 'Êtes-vous certain de vouloir supprimer l\'accès logiciel ?',
        okText: 'Supprimer',
        okType: 'danger',
        cancelText: 'Annuler',
        width: '40%',
        onOk: async () => {
          setDeletingId(softaccessId)
          try {
            await axios.delete(deleteUrl)
            message.success('Accès supprimé')
            await loadAccesses()
          } catch (error) {
            console.error(error)
            message.error('Erreur lors de la suppression')
          } finally {
            setDeletingId(null)
          }
        },
      })
    },
    [loadAccesses],
  )

  const cnAccess = data?.cn?.[0] ?? null
  const conquestAccesses = data?.conquest || []

  return (
    <div style={{ marginTop: 16, display: 'grid', backgroundColor: '#f7f7f7', padding: 25, borderRadius: 20 }}>

      <>
        <Space
          align="center"
          style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Accès "Scan & Conquest" / "MS Pro"
          </Title>
          {cnAccess ? <ExportCn orgaId={orgaId} /> : null}
        </Space>

        <Card style={{ marginBottom: 32, marginTop: 16 }} loading={loading} hoverable={cnAccess ? true : false} onClick={() => cnAccess ? window.location.href = `/organisations/${orgaId}/cn/softwaresaccesses/${cnAccess.softaccess_id}` : null}>
          {cnAccess ? (
            <Space
              align="center"
              style={{ width: '100%', justifyContent: 'space-between' }}
              wrap
            >
              <Title level={5} style={{ margin: 0 }}>{cnAccess?.mspro ? "MS Pro" : "Scan & Conquest"}</Title>
              <Tag icon={<CheckCircleOutlined />} color="success">
                Accès actif
              </Tag>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    loading={deletingId === cnAccess.softaccess_id}
                    onClick={(e) => {
                      e.stopPropagation()
                      confirmDelete(
                        cnAccess.softaccess_id,
                        `/organisations/${orgaId}/cn/softwaresaccesses/${cnAccess.softaccess_id}`,
                      )
                    }}
                  >
                    Supprimer
                  </Button>
                }
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  onClick={(event) => event.stopPropagation()}
                />
              </Popover>
            </Space>)
            : (<div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <Title level={5} style={{ marginBottom: 8 }}>
                Aucun accès configuré
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                1 accès maximum par organisation
              </Text>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                href={`/organisations/${orgaId}/cn/softwaresaccesses/new`}
              >
                Créer l&apos;accès
              </Button>
            </div>)}
        </Card>
      </>

              <Divider />
              <br />

      <Space align="center" style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={4} style={{ margin: 0 }}>
          Conquest
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          href={`/organisations/${orgaId}/conquest/softwaresaccesses/new`}
        >
          Ajouter un accès
        </Button>
      </Space>
      <br /><br />
      {loading && conquestAccesses.length === 0 ? (
        <Card loading />
      ) : conquestAccesses.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '24px 16px' }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              Aucun accès configuré
            </Title>
          </div>
        </Card>
      ) : (
        conquestAccesses.map((record) => (
          <Card
            key={record.softaccess_id}
            style={{ marginBottom: 16 }}
            hoverable
            onClick={() => {
              window.location.href = `/organisations/${orgaId}/conquest/softwaresaccesses/${record.softaccess_id}`
            }}
          >
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
              <Title level={5} style={{ margin: 0 }}>
                {record.name}
              </Title>
              <Row>
                <Button
                  style={{ marginRight: 20 }}
                  type="primary"
                  href={`/organisations/${orgaId}/conquest/softwaresaccesses/${record.softaccess_id}/access`}
                >
                  Accès rg4
                </Button>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  content={
                    <Space direction="vertical" size={0}>

                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        loading={deletingId === record.softaccess_id}
                        onClick={(event) => {
                          event.stopPropagation()
                          confirmDelete(
                            record.softaccess_id,
                            `/organisations/${orgaId}/conquest/softwaresaccesses/${record.softaccess_id}`,
                          )
                        }}
                      >
                        Supprimer
                      </Button>
                    </Space>
                  }
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Popover>
              </Row>
            </Space>
          </Card>
        ))
      )}
    </div>
  )
}
